import { useEffect, useLayoutEffect } from "react";
import { useColorMode } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { isMarketingSitePath } from "../pages/marketing-site/marketingSiteRoutes";
import { getCurrentDayPhase } from "./dayNightSchedule";
import {
  applyColorThemeToDocument,
  getColorThemeScopeForPath,
  isLearnerPagePath,
  isTwilightSceneShowing,
  markDocumentWhileKeyboardIsInUse,
  readStoredAdminToolsColorMode,
  resolveColorTheme,
} from "./jungleColorTheme";
import { tellMobileAppTheColorTheme } from "./mobileAppColorThemeBridge";
import { useJungleColorThemeStore } from "./useJungleColorThemeStore";

// Keeps the page on the right theme: resolves it for the current route, writes it to <html>, and tells
// Chakra. Renders nothing. Mounted once inside the router (it needs the current path) and inside
// ChakraProvider (it sets Chakra's color mode).
export function JungleColorThemeSync() {
  const { pathname } = useLocation();
  const { colorMode, setColorMode } = useColorMode();
  const colorThemePreference = useJungleColorThemeStore((state) => state.colorThemePreference);
  const dayPhase = useJungleColorThemeStore((state) => state.dayPhase);
  const setDayPhase = useJungleColorThemeStore((state) => state.setDayPhase);
  const setArtworkState = useJungleColorThemeStore((state) => state.setArtworkState);

  const colorThemeScope = getColorThemeScopeForPath(pathname, isMarketingSitePath(pathname));
  const isLearnerPage = isLearnerPagePath(pathname, isMarketingSitePath(pathname));
  const isInAdminTools = colorThemeScope === "adminTools";

  // Arriving in the admin area: hand Chakra the choice its own switch last stored. From then on Chakra's
  // color mode is the admin's to change, so nothing below overrides it.
  useLayoutEffect(() => {
    if (isInAdminTools) setColorMode(readStoredAdminToolsColorMode());
  }, [isInAdminTools, setColorMode]);

  const colorTheme = resolveColorTheme({
    colorThemeScope,
    colorThemePreference,
    dayPhase,
    adminToolsColorMode: colorMode === "dark" ? "dark" : "light",
  });
  const isTwilightScene = isTwilightSceneShowing({ colorThemeScope, colorThemePreference, dayPhase });

  // Before paint, so a route that changes theme (leaving a dark study page for a marketing page) never
  // shows one frame of the wrong colors.
  useLayoutEffect(() => {
    applyColorThemeToDocument({ colorTheme, colorThemeScope, isTwilightScene, isLearnerPage });
    setArtworkState({
      isDarkThemeActive: colorTheme === "dark" && colorThemeScope === "app",
      isTwilightSceneActive: isTwilightScene,
    });
    if (colorMode !== colorTheme) setColorMode(colorTheme);
  }, [colorTheme, colorThemeScope, isTwilightScene, isLearnerPage, colorMode, setColorMode, setArtworkState]);

  // Inside the mobile app the bars around the page are the app's to paint; it hears every change of theme
  useEffect(() => tellMobileAppTheColorTheme(colorTheme), [colorTheme]);

  // Follow the sun while it is the one deciding: the automatic appearance turns dark at night and light in
  // the morning, with a twilight hour before and after. Checked every minute, and at once when the tab
  // comes back to the front (a laptop opened in the evening on a page left open since noon).
  useEffect(() => {
    const followTimeOfDay = () => setDayPhase(getCurrentDayPhase());
    followTimeOfDay();
    const everyMinute = window.setInterval(followTimeOfDay, 60_000);
    document.addEventListener("visibilitychange", followTimeOfDay);
    return () => {
      window.clearInterval(everyMinute);
      document.removeEventListener("visibilitychange", followTimeOfDay);
    };
  }, [setDayPhase]);

  // The dark themes' keyboard ring shows only while the Tab key is in use (see the function)
  useEffect(() => markDocumentWhileKeyboardIsInUse(), []);

  return null;
}
